/**
 * ProjectTeamPanel — Main container for project role slot management
 *
 * Shows role slots grouped by phase (preparation, operational, sustainment),
 * a summary bar with fill counts, and action buttons for adding/editing roles.
 *
 * Used on the Management tab "Team" sub-tab.
 * Future: also used in workspace persistent utility and config modal.
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiProjectRoles } from "../../api/useApiProjectRoles";
import { invalidate } from "../../api/queryCache";
import {
  createProjectRoleSlot,
  updateProjectRoleSlot,
  closeProjectRoleSlot,
  reopenProjectRoleSlot,
  assignUserToRole,
  updateRoleAssignment,
  removeRoleAssignment,
  reorderProjectRoleSlots,
} from "../../api/projectRoles";
import type {
  ProjectRoleSlotResponse,
  ProjectRoleAssignmentResponse,
  CreateProjectRoleSlotRequest,
  UpdateProjectRoleSlotRequest,
} from "../../api/projectRoles";
import { fetchAllSecurityUsers, SecurityUserResponse } from "../../api/security";
import { permissionsApiV2 } from "../../security/v2/apiClient";
import type { PermissionGrantDto, PermissionSetDto } from "../../security/v2/types";
import RoleSlotCard from "./RoleSlotCard";
import RoleSlotEditor from "./RoleSlotEditor";
import type { RoleSlotFormData } from "./RoleSlotEditor";
import RoleAssignmentModal from "./RoleAssignmentModal";
import RoleAssignmentDetailModal from "../RoleAssignmentDetailModal/RoleAssignmentDetailModal";
import { useRoleDetailData } from "../RoleAssignmentDetailModal/useRoleDetailData";
import PendingApplicationsPanel from "./PendingApplicationsPanel";
import VerificationPanel from "./VerificationPanel";
import TeamStructureView from "./TeamStructureView";
import TeamRaciView from "./TeamRaciView";
import RoleDigestView from "./RoleDigestView";
import { getCurrentUserGuid } from "../../api/client";
import { useApiCommunityData } from "../../api";
import { normalizeGuid } from "../../utils/guid";
import type { RolePhase } from "./projectTeamTypes";
import styles from "./ProjectTeamPanel.module.css";

const PHASE_ORDER: RolePhase[] = ["preparation", "operational", "sustainment"];

interface Props {
  projectGuid: string;
  projectName: string;
  ownerGuid: string;
  canEdit: boolean;
  debugMode?: boolean;
  /** When provided, the panel starts in this view mode (e.g. 'digest' from Engagements). */
  initialViewMode?: string;
}

const ProjectTeamPanel: React.FC<Props> = ({
  projectGuid,
  projectName,
  ownerGuid,
  canEdit,
  debugMode,
  initialViewMode,
}) => {
  const { t } = useAppTranslation("projectTeam");
  const { slots, closedSlots, summary, loading, error, refetch } = useApiProjectRoles(projectGuid);

  // ── User map for avatars ──
  const [allUsers, setAllUsers] = useState<SecurityUserResponse[]>([]);
  useEffect(() => {
    fetchAllSecurityUsers()
      .then((users) => setAllUsers(users.filter((u) => u.isActive)))
      .catch(() => {});
  }, []);

  // ── DEV-1042 / DEV-1043. Slot grants on this project — used to pre-fill
  //    the bundle picker when editing an existing slot, AND to drive the
  //    revoke-old / create-new lifecycle in handleSaveRole / handleCloseSlot.
  //    Fetched on mount; manually re-fetched after slot CRUD via
  //    refetchSlotGrants. Endpoint requires admin authority; non-admins
  //    silently get an empty list (the editor isn't shown to them either way).
  const [slotGrants, setSlotGrants] = useState<PermissionGrantDto[]>([]);
  const refetchSlotGrants = useCallback(async () => {
    if (!projectGuid || !canEdit) {
      setSlotGrants([]);
      return;
    }
    try {
      const grants = await permissionsApiV2.listGrantsForScope("project", projectGuid);
      setSlotGrants(grants);
    } catch {
      // 403 for non-admins, network errors, etc. Silently fall back to
      // no pre-fill — the picker will show blank and the user re-selects.
    }
  }, [projectGuid, canEdit]);
  useEffect(() => {
    refetchSlotGrants();
  }, [refetchSlotGrants]);

  // ── DEV-1044. Available bundles on this project — used to resolve
  //    permissionSetGuid → human-readable bundle name on every slot card
  //    (and the Structure view nodes). Includes ALL bundles, not just
  //    operational, so name lookups work even for legacy/mismatched grants.
  //    Endpoint is public-read; no canEdit gate.
  const [allBundles, setAllBundles] = useState<PermissionSetDto[]>([]);
  useEffect(() => {
    if (!projectGuid) return;
    let cancelled = false;
    permissionsApiV2
      .listPermissionSetsForScope("project", projectGuid)
      .then((bundles) => {
        if (!cancelled) setAllBundles(bundles);
      })
      .catch(() => {
        // Silent fallback — bundle names just won't render. Slot cards
        // still work; "No bundle assigned" indicator surfaces instead.
      });
    return () => {
      cancelled = true;
    };
  }, [projectGuid]);

  const userMap = useMemo(() => {
    const map = new Map<string, SecurityUserResponse>();
    allUsers.forEach((u) => map.set(u.userGuid.toUpperCase(), u));
    return map;
  }, [allUsers]);

  // DEV-1039 iter 3 follow-up. GUID→name map of community entities, used by
  // RoleAssignmentModal to resolve the picked user's primaryLocationEntityGuid
  // into a readable "Home Community" label inside the IdentityCard. Same
  // source of truth ProjectConfigModal uses for the Admins tray.
  const apiCommunityData = useApiCommunityData();
  const communityNameByGuid = useMemo(() => {
    const m = new Map<string, string>();
    const entities = apiCommunityData.communityEntities || [];
    for (const e of entities) {
      const guid = normalizeGuid((e as any)?.entity_guid);
      const name = String((e as any)?.name ?? "").trim();
      if (guid && name) m.set(guid, name);
    }
    return m;
  }, [apiCommunityData.communityEntities]);

  // ── Modal state ──
  const [editingSlot, setEditingSlot] = useState<ProjectRoleSlotResponse | null>(null);
  const [assigningSlotGuid, setAssigningSlotGuid] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [detailAssignment, setDetailAssignment] = useState<{
    assignment: ProjectRoleAssignmentResponse;
    slot: ProjectRoleSlotResponse;
  } | null>(null);

  // Fetch tasks + hierarchy for the selected role detail
  const roleDetailData = useRoleDetailData({
    projectGuid: detailAssignment ? projectGuid : null,
    roleSlotGuid: detailAssignment?.slot.roleSlotGuid ?? null,
    roleLabel: detailAssignment?.slot.roleLabel ?? '',
    projectName,
  });

  // Keep assigningSlot in sync with latest slot data from API
  const assigningSlot = useMemo(
    () => assigningSlotGuid
      ? slots.find((s) => s.roleSlotGuid.toUpperCase() === assigningSlotGuid.toUpperCase()) ?? null
      : null,
    [assigningSlotGuid, slots]
  );

  // ── Group slots by phase ──
  const groupedSlots = useMemo(() => {
    const groups: Record<string, ProjectRoleSlotResponse[]> = {};
    for (const phase of PHASE_ORDER) {
      groups[phase] = [];
    }
    for (const slot of slots) {
      const phase = slot.phase as RolePhase;
      if (groups[phase]) {
        groups[phase].push(slot);
      } else {
        groups["operational"].push(slot);
      }
    }
    return groups;
  }, [slots]);

  const draftSlots = useMemo(
    () => slots.filter((s) => s.status === "draft"),
    [slots]
  );

  const nonDraftSlots = useMemo(
    () => slots.filter((s) => s.status !== "draft"),
    [slots]
  );

  // ── View mode: Cards vs Structure vs RACI vs Digest ──
  type ViewMode = "cards" | "structure" | "raci" | "digest";
  const validViews: ViewMode[] = ["cards", "structure", "raci", "digest"];
  const resolvedInitial: ViewMode = initialViewMode && validViews.includes(initialViewMode as ViewMode)
    ? (initialViewMode as ViewMode)
    : "cards";
  const [viewMode, setViewMode] = useState<ViewMode>(resolvedInitial);

  // ── Current user guid for Digest view ──
  const currentUserGuid = useMemo(() => getCurrentUserGuid(), []);

  // ── Role slot guids the current user is assigned to (for Digest) ──
  const myRoleSlotGuids = useMemo(() => {
    const uid = currentUserGuid.toUpperCase();
    return slots
      .filter((s) =>
        s.assignments?.some(
          (a) => a.userGuid?.toUpperCase() === uid && a.status === "active"
        )
      )
      .map((s) => s.roleSlotGuid);
  }, [slots, currentUserGuid]);

  /** True if at least one slot has a reports_to relationship. */
  const hasHierarchy = useMemo(
    () => slots.some((s) => s.reportsToSlotGuid != null),
    [slots]
  );

  // DEV-1042. Bundle currently granted to the slot being edited (if any).
  // Passed to RoleSlotEditor to pre-fill the bundle picker. Looks for an
  // active slot grant on this slot; falls back to null if none exists.
  // GUID comparison is case-insensitive per the codebase convention.
  const currentBundleGuidForEditing = useMemo(() => {
    if (!editingSlot) return null;
    const target = editingSlot.roleSlotGuid.toUpperCase();
    const grant = slotGrants.find(
      (g) =>
        g.roleSlotGuid != null &&
        g.roleSlotGuid.toUpperCase() === target &&
        // Treat undefined status as active for backward compat with
        // payloads from before DEV-892 (the status column was nullable).
        (g.status === undefined || g.status === "active")
    );
    return grant?.permissionSetGuid ?? null;
  }, [editingSlot, slotGrants]);

  // DEV-1044. Map of slot GUID (uppercase) → bundle display name, joined
  // from slotGrants and allBundles. Used by RoleSlotCard and the Structure
  // view nodes to render "what authority does this role carry" at a glance.
  // Slots without an active grant are absent from the map; consumers render
  // a "No bundle assigned" indicator in that case.
  const slotBundleNameMap = useMemo(() => {
    const bundleNameByGuid = new Map<string, string>();
    for (const b of allBundles) {
      bundleNameByGuid.set(b.permissionSetGuid.toUpperCase(), b.name);
    }
    const map = new Map<string, string>();
    for (const g of slotGrants) {
      if (!g.roleSlotGuid) continue;
      if (g.status !== undefined && g.status !== "active") continue;
      const name = bundleNameByGuid.get(g.permissionSetGuid.toUpperCase());
      if (name) map.set(g.roleSlotGuid.toUpperCase(), name);
    }
    return map;
  }, [allBundles, slotGrants]);

  // ── Handlers ──
  const handleToggleVisibility = useCallback(
    async (slot: ProjectRoleSlotResponse) => {
      const newVisibility = slot.visibility === "public" ? "internal" : "public";
      try {
        await updateProjectRoleSlot(projectGuid, slot.roleSlotGuid, {
          visibility: newVisibility,
        });
        await refetch();
      } catch (err) {
        console.error("[TeamPanel] Toggle visibility failed:", err);
      }
    },
    [projectGuid, refetch]
  );

  const handleCloseSlot = useCallback(
    async (slot: ProjectRoleSlotResponse) => {
      setCloseError(null);
      try {
        await closeProjectRoleSlot(projectGuid, slot.roleSlotGuid);

        // DEV-1043. Revoke the slot's bundle grant AFTER slot close. Order
        // matters: close fails when active user assignments exist, in
        // which case we must NOT revoke the grant — those users would
        // silently lose their caps. Only when close succeeds is the slot
        // confirmed empty of users and the bundle grant is safe to drop.
        const targetGuid = slot.roleSlotGuid.toUpperCase();
        const slotGrant = slotGrants.find(
          (g) =>
            g.roleSlotGuid != null &&
            g.roleSlotGuid.toUpperCase() === targetGuid &&
            (g.status === undefined || g.status === "active")
        );
        if (slotGrant) {
          try {
            await permissionsApiV2.revokeGrant(slotGrant.grantGuid);
          } catch (err) {
            // Non-fatal: slot is closed, grant cleanup failed. The grant
            // points at a now-closed slot which the evaluator should
            // already skip. File a cleanup job if this becomes a pattern.
            console.error("[TeamPanel] Revoke grant on close failed:", err);
          }
        }

        await refetch();
        await refetchSlotGrants();
      } catch (err: any) {
        // Surface the guard error when active assignments exist
        const body = err?.body || err?.data;
        if (body?.error === "ACTIVE_ASSIGNMENTS") {
          setCloseError(t("editor.deleteBlockedActiveAssignments"));
        } else {
          setCloseError(t("editor.deleteFailed"));
        }
        console.error("[TeamPanel] Close slot failed:", err);
        throw err; // Re-throw so RoleSlotEditor knows the delete failed
      }
    },
    [projectGuid, refetch, refetchSlotGrants, slotGrants, t]
  );

  const handleReopenSlot = useCallback(
    async (slot: ProjectRoleSlotResponse) => {
      try {
        await reopenProjectRoleSlot(projectGuid, slot.roleSlotGuid);
        await refetch();
      } catch (err) {
        console.error("[TeamPanel] Reopen slot failed:", err);
      }
    },
    [projectGuid, refetch]
  );

  const handleActivateDrafts = useCallback(async () => {
    try {
      for (const slot of draftSlots) {
        await updateProjectRoleSlot(projectGuid, slot.roleSlotGuid, {
          status: "open",
        });
      }
      await refetch();
    } catch (err) {
      console.error("[TeamPanel] Activate drafts failed:", err);
    }
  }, [projectGuid, draftSlots, refetch]);

  // ── Save handler (create or update) ──
  const handleSaveRole = useCallback(
    async (data: RoleSlotFormData) => {
      try {
        // DEV-1043. Capture current bundle for diffing in the update branch.
        // For new slots, previousBundleGuid is null and any chosen bundle
        // results in a new grant.
        const previousBundleGuid = editingSlot ? currentBundleGuidForEditing : null;
        const newBundleGuid = data.permissionSetGuid || null;
        let targetSlotGuid: string;

        if (editingSlot) {
          // Determine if we're clearing the parent (had one before, now null)
          const hadParent = !!editingSlot.reportsToSlotGuid;
          const clearReportsTo = hadParent && !data.reportsToSlotGuid;

          const updateReq: UpdateProjectRoleSlotRequest = {
            roleCategory: data.roleCategory,
            roleLabel: data.roleLabel,
            description: data.description || undefined,
            phase: data.phase,
            slotsNeeded: data.slotsNeeded,
            visibility: data.visibility,
            isOwnerDefault: data.isOwnerDefault,
            dimensionWeights: data.dimensionWeights
              ? JSON.stringify(data.dimensionWeights)
              : undefined,
            skillsRequired: data.skillsRequired,
            timeCommitment: data.timeCommitment || undefined,
          };

          // Team hierarchy — only include when user explicitly set a value
          if (clearReportsTo) {
            updateReq.clearReportsTo = true;
          } else if (data.reportsToSlotGuid) {
            updateReq.reportsToSlotGuid = data.reportsToSlotGuid;
          }

          console.info("[TeamPanel] Saving role update:", JSON.stringify(updateReq));
          await updateProjectRoleSlot(projectGuid, editingSlot.roleSlotGuid, updateReq);
          targetSlotGuid = editingSlot.roleSlotGuid;
        } else {
          // Create new
          const createReq: CreateProjectRoleSlotRequest = {
            sourceDefinitionGuid: data.sourceDefinitionGuid,
            roleCategory: data.roleCategory,
            roleLabel: data.roleLabel,
            description: data.description || undefined,
            phase: data.phase,
            slotsNeeded: data.slotsNeeded,
            status: "open",
            visibility: data.visibility,
            isOwnerDefault: data.isOwnerDefault,
            dimensionWeights: data.dimensionWeights
              ? JSON.stringify(data.dimensionWeights)
              : undefined,
            skillsRequired: data.skillsRequired,
            timeCommitment: data.timeCommitment || undefined,
          };

          if (data.reportsToSlotGuid) {
            createReq.reportsToSlotGuid = data.reportsToSlotGuid;
          }

          const newSlot = await createProjectRoleSlot(projectGuid, createReq);
          targetSlotGuid = newSlot.roleSlotGuid;
        }

        // DEV-1043. Bundle grant lifecycle.
        // - Revoke old grant if editing AND bundle changed AND old existed
        // - Create new grant if newBundleGuid is set AND it differs from old
        // GUID comparison is case-insensitive per codebase convention.
        const previousNorm = (previousBundleGuid ?? "").toUpperCase();
        const newNorm = (newBundleGuid ?? "").toUpperCase();
        const bundleChanged = previousNorm !== newNorm;

        if (bundleChanged && editingSlot && previousBundleGuid) {
          const oldGrant = slotGrants.find(
            (g) =>
              g.roleSlotGuid != null &&
              g.roleSlotGuid.toUpperCase() === targetSlotGuid.toUpperCase() &&
              g.permissionSetGuid.toUpperCase() === previousNorm
          );
          if (oldGrant) {
            try {
              await permissionsApiV2.revokeGrant(oldGrant.grantGuid);
            } catch (err) {
              // Non-fatal: continue to create the new grant. If both
              // grants end up on the slot, the evaluator picks the first
              // active one; we'll catch the duplicate in cleanup.
              console.error("[TeamPanel] Revoke previous bundle grant failed:", err);
            }
          }
        }

        if (bundleChanged && newBundleGuid) {
          try {
            await permissionsApiV2.createGrantForScope("project", projectGuid, {
              permissionSetGuid: newBundleGuid,
              roleSlotGuid: targetSlotGuid,
              userGuid: null,
            });
          } catch (err) {
            // Non-fatal: slot was saved but bundle attachment failed. User
            // can retry via edit. Surface to console so the failure is
            // visible without crashing the editor.
            console.error("[TeamPanel] Create bundle grant failed:", err);
          }
        }

        await refetch();
        await refetchSlotGrants();
      } catch (err) {
        console.error("[TeamPanel] Save role failed:", err);
      } finally {
        // Always close the modal, even on error
        setEditingSlot(null);
        setShowAddForm(false);
      }
    },
    [projectGuid, editingSlot, refetch, refetchSlotGrants, currentBundleGuidForEditing, slotGrants]
  );

  const handleSaveAndAdd = useCallback(
    async (data: RoleSlotFormData) => {
      try {
        const newSlot = await createProjectRoleSlot(projectGuid, {
          sourceDefinitionGuid: data.sourceDefinitionGuid,
          roleCategory: data.roleCategory,
          roleLabel: data.roleLabel,
          description: data.description || undefined,
          phase: data.phase,
          slotsNeeded: data.slotsNeeded,
          status: "open",
          visibility: data.visibility,
          isOwnerDefault: data.isOwnerDefault,
          dimensionWeights: data.dimensionWeights
            ? JSON.stringify(data.dimensionWeights)
            : undefined,
          skillsRequired: data.skillsRequired,
          timeCommitment: data.timeCommitment || undefined,
          reportsToSlotGuid: data.reportsToSlotGuid || undefined,
        });

        // DEV-1043. Bundle grant on new slot (mirrors handleSaveRole's create branch).
        if (data.permissionSetGuid) {
          try {
            await permissionsApiV2.createGrantForScope("project", projectGuid, {
              permissionSetGuid: data.permissionSetGuid,
              roleSlotGuid: newSlot.roleSlotGuid,
              userGuid: null,
            });
          } catch (err) {
            console.error("[TeamPanel] Create bundle grant failed:", err);
          }
        }

        await refetch();
        await refetchSlotGrants();
        // Form stays open for next entry (RoleSlotEditor resets internally)
      } catch (err) {
        console.error("[TeamPanel] Save & add failed:", err);
      }
    },
    [projectGuid, refetch, refetchSlotGrants]
  );

  // ── Assignment handlers ──
  const handleAssignUser = useCallback(
    async (userGuid: string) => {
      if (!assigningSlotGuid) return;
      try {
        await assignUserToRole(projectGuid, assigningSlotGuid, {
          userGuid,
          assignedBy: ownerGuid, // Team lead assigning = direct assignment, no approval needed
        });
        await refetch();
        // Keep modal open so user can assign more
      } catch (err) {
        console.error("[TeamPanel] Assign failed:", err);
      }
    },
    [projectGuid, assigningSlotGuid, ownerGuid, refetch]
  );

  const handleInviteUser = useCallback(
    async (userGuid: string, invitationMessage?: string) => {
      if (!assigningSlotGuid) return;
      try {
        await assignUserToRole(projectGuid, assigningSlotGuid, {
          userGuid,
          assignedBy: ownerGuid,
          isInvitation: true,
          invitationMessage: invitationMessage || undefined,
        });
        await refetch();
      } catch (err) {
        console.error("[TeamPanel] Invite failed:", err);
      }
    },
    [projectGuid, assigningSlotGuid, ownerGuid, refetch]
  );

  const handleRescindInvitation = useCallback(
    async (assignmentGuid: string) => {
      if (!assigningSlotGuid) return;
      try {
        // DEV-1039 iter 3 — Cycle 1 fix. Rescind path was silently
        // swallowing errors and the modal couldn't tell the action had
        // failed, leaving the row visible. Now re-throws so the modal's
        // optimistic-UI rollback can surface the failure.
        // Backend soft-deletes (sets status to 'removed' or 'withdrawn');
        // the slot still returns to assignable because the status filter
        // excludes those values.
        await removeRoleAssignment(
          projectGuid,
          assigningSlotGuid,
          assignmentGuid
        );
        // Belt + suspenders cache bust. force:true on refetch should
        // be enough, but invalidate the cache key explicitly first in
        // case any other subscriber holds a stale read.
        invalidate(`projectRoles:${projectGuid}`);
        await refetch();
      } catch (err) {
        console.error("[TeamPanel] Rescind invitation failed:", err);
        throw err;
      }
    },
    [projectGuid, assigningSlotGuid, refetch]
  );

  const handleRemoveAssignment = useCallback(
    async (assignmentGuid: string, reason?: string) => {
      if (!assigningSlotGuid) return;
      try {
        // Soft-remove: transition status to "removed" with reason,
        // preserving the assignment record for history.
        await updateRoleAssignment(
          projectGuid,
          assigningSlotGuid,
          assignmentGuid,
          {
            status: "removed",
            ...(reason ? { notes: reason } : {}),
          }
        );
        await refetch();
      } catch (err) {
        console.error("[TeamPanel] Remove assignment failed:", err);
      }
    },
    [projectGuid, assigningSlotGuid, refetch]
  );

  const handleUpdateVisibility = useCallback(
    async (assignmentGuid: string, visibility: string) => {
      if (!assigningSlotGuid) return;
      try {
        await updateRoleAssignment(
          projectGuid,
          assigningSlotGuid,
          assignmentGuid,
          { visibility }
        );
        await refetch();
      } catch (err) {
        console.error("[TeamPanel] Update visibility failed:", err);
      }
    },
    [projectGuid, assigningSlotGuid, refetch]
  );

  // ── Drag-and-drop reordering ──
  const [dragSlotGuid, setDragSlotGuid] = useState<string | null>(null);
  const [dragOverSlotGuid, setDragOverSlotGuid] = useState<string | null>(null);

  const handleDragStart = useCallback(
    (slotGuid: string) => (e: React.DragEvent) => {
      setDragSlotGuid(slotGuid);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", slotGuid);
    },
    []
  );

  const handleDragEnd = useCallback(() => {
    setDragSlotGuid(null);
    setDragOverSlotGuid(null);
  }, []);

  const handleDragOver = useCallback(
    (slotGuid: string) => (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverSlotGuid(slotGuid);
    },
    []
  );

  const handleDragLeave = useCallback(() => {
    setDragOverSlotGuid(null);
  }, []);

  const handleDrop = useCallback(
    (targetSlotGuid: string, phaseSlots: ProjectRoleSlotResponse[]) =>
      async (e: React.DragEvent) => {
        e.preventDefault();
        setDragSlotGuid(null);
        setDragOverSlotGuid(null);

        const sourceGuid = e.dataTransfer.getData("text/plain");
        if (!sourceGuid || sourceGuid === targetSlotGuid) return;

        // Find indices within the phase group
        const sourceIdx = phaseSlots.findIndex(
          (s) => s.roleSlotGuid.toUpperCase() === sourceGuid.toUpperCase()
        );
        const targetIdx = phaseSlots.findIndex(
          (s) => s.roleSlotGuid.toUpperCase() === targetSlotGuid.toUpperCase()
        );
        if (sourceIdx === -1 || targetIdx === -1) return;

        // Reorder: remove source, insert at target position
        const reordered = [...phaseSlots];
        const [moved] = reordered.splice(sourceIdx, 1);
        reordered.splice(targetIdx, 0, moved);

        // Build sort_order updates
        const updates = reordered.map((slot, idx) => ({
          roleSlotGuid: slot.roleSlotGuid,
          sortOrder: idx,
        }));

        try {
          await reorderProjectRoleSlots(projectGuid, updates);
          await refetch();
        } catch (err) {
          console.error("[TeamPanel] Reorder failed:", err);
        }
      },
    [projectGuid, refetch]
  );

  // ── Render ──
  if (loading) {
    return <div className={styles.loading}>{t("panel.loading")}</div>;
  }

  if (error) {
    return (
      <div className={styles.loading}>
        {t("panel.error")}: {error}
      </div>
    );
  }

  // Empty state (but still render modals below)
  if (slots.length === 0 && !showAddForm && !editingSlot) {
    return (
      <>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>&#x1F465;</span>
          <span className={styles.emptyText}>{t("panel.emptyText")}</span>
          {canEdit && (
            <button
              type="button"
              className={styles.emptyAction}
              onClick={() => setShowAddForm(true)}
            >
              {t("panel.addFirstRole")}
            </button>
          )}
        </div>

        {/* Role editor modal (must render even in empty state) */}
        {(showAddForm || editingSlot) && (
          <RoleSlotEditor
            mode="project"
            existingSlot={editingSlot}
            allSlots={slots}
            projectGuid={projectGuid}
            currentBundleGuid={currentBundleGuidForEditing}
            onSave={handleSaveRole}
            onSaveAndAdd={!editingSlot ? handleSaveAndAdd : undefined}
            onClose={() => {
              setShowAddForm(false);
              setEditingSlot(null);
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className={styles.panel}>
      {/* Summary bar */}
      {summary && (
        <div className={styles.summaryBar}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.totalSlots}</span>
            {t("panel.roles")}
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{summary.totalPeopleNeeded}</span>
            {t("panel.positions")}
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>
              {summary.totalPeopleFilled}/{summary.totalPeopleNeeded}
            </span>
            {t("panel.positionsFilled")}
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>
              {Math.max(0, summary.totalPeopleNeeded - summary.totalPeopleFilled)}
            </span>
            {t("panel.openings")}
          </div>
          {canEdit && summary.draftSlots > 0 && (
            <>
              <div className={styles.summaryDivider} />
              <div className={styles.summaryItem}>
                <span className={styles.summaryValue}>{summary.draftSlots}</span>
                {t("panel.draft")}
              </div>
            </>
          )}
          {/* View toggle: Cards / Structure / RACI */}
          {(hasHierarchy || slots.length > 0) && (
            <div className={styles.viewToggle}>
              <button
                type="button"
                className={`${styles.viewToggleBtn} ${viewMode === "cards" ? styles.viewToggleActive : ""}`}
                onClick={() => setViewMode("cards")}
              >
                {t("panel.cardsView")}
              </button>
              {hasHierarchy && (
                <button
                  type="button"
                  className={`${styles.viewToggleBtn} ${viewMode === "structure" ? styles.viewToggleActive : ""}`}
                  onClick={() => setViewMode("structure")}
                >
                  {t("panel.treeView")}
                </button>
              )}
              <button
                type="button"
                className={`${styles.viewToggleBtn} ${viewMode === "raci" ? styles.viewToggleActive : ""}`}
                onClick={() => setViewMode("raci")}
              >
                {t("panel.raciView")}
              </button>
              {myRoleSlotGuids.length > 0 && (
                <button
                  type="button"
                  className={`${styles.viewToggleBtn} ${viewMode === "digest" ? styles.viewToggleActive : ""}`}
                  onClick={() => setViewMode("digest")}
                >
                  {t("digest.digestView")}
                </button>
              )}
            </div>
          )}

          {canEdit && (
            <button
              type="button"
              className={styles.addBtn}
              onClick={() => setShowAddForm(true)}
            >
              + {t("panel.addRole")}
            </button>
          )}
        </div>
      )}

      {/* Pending applications (PM review — volunteer sign-ups) */}
      {canEdit && (
        <PendingApplicationsPanel
          projectGuid={projectGuid}
          slots={slots}
          userMap={userMap}
          onActionComplete={refetch}
        />
      )}

      {/* Pending verifications (PM review — role completions) */}
      {canEdit && (
        <VerificationPanel
          projectGuid={projectGuid}
          slots={slots}
          userMap={userMap}
          onActionComplete={refetch}
        />
      )}

      {/* Draft banner */}
      {draftSlots.length > 0 && canEdit && (
        <div className={styles.draftBanner}>
          <span className={styles.draftCount}>{draftSlots.length}</span>
          {t("panel.draftRoles")}
          <button
            type="button"
            className={styles.activateBtn}
            onClick={handleActivateDrafts}
          >
            {t("panel.activateAll")}
          </button>
        </div>
      )}

      {/* ── Structure view (org chart) ── */}
      {viewMode === "structure" && hasHierarchy && (
        <TeamStructureView
          projectGuid={projectGuid}
          projectName={projectName}
          slots={slots}
          userMap={userMap}
          ownerGuid={ownerGuid}
          canEdit={canEdit}
          slotBundleNameMap={slotBundleNameMap}
          onRefetch={refetch}
          onEditSlot={(s) => setEditingSlot(s)}
          onAssignSlot={(s) => setAssigningSlotGuid(s.roleSlotGuid)}
          onViewAssignment={(a, s) => setDetailAssignment({ assignment: a, slot: s })}
        />
      )}

      {/* ── Cards view (default, grouped by phase) ── */}
      {viewMode === "cards" && (
        <>
          {PHASE_ORDER.map((phase) => {
            const phaseSlots = groupedSlots[phase].filter(
              (s) => s.status !== "draft"
            );
            if (phaseSlots.length === 0) return null;

            return (
              <div key={phase} className={styles.phaseGroup}>
                <div className={styles.phaseHeader}>
                  {t(`phases.${phase}`)}
                  <span className={styles.phaseCount}>
                    ({phaseSlots.length})
                  </span>
                </div>
                <div className={styles.slotList}>
                  {phaseSlots.map((slot) => (
                    <RoleSlotCard
                      key={slot.roleSlotGuid}
                      slot={slot}
                      userMap={userMap}
                      ownerGuid={ownerGuid}
                      canEdit={canEdit}
                      bundleName={slotBundleNameMap.get(slot.roleSlotGuid.toUpperCase()) ?? null}
                      onEdit={(s) => setEditingSlot(s)}
                      onAssign={(s) => setAssigningSlotGuid(s.roleSlotGuid)}
                      onClose={handleCloseSlot}
                      onToggleVisibility={handleToggleVisibility}
                      onViewAssignment={(a, s) => setDetailAssignment({ assignment: a, slot: s })}
                      isDragging={dragSlotGuid === slot.roleSlotGuid}
                      isDragOver={dragOverSlotGuid === slot.roleSlotGuid}
                      onDragStart={handleDragStart(slot.roleSlotGuid)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver(slot.roleSlotGuid)}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop(slot.roleSlotGuid, phaseSlots)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Draft slots section */}
          {draftSlots.length > 0 && (
            <div className={styles.phaseGroup}>
              <div className={styles.phaseHeader}>
                {t("panel.drafts")}
                <span className={styles.phaseCount}>
                  ({draftSlots.length})
                </span>
              </div>
              <div className={styles.slotList}>
                {draftSlots.map((slot) => (
                  <RoleSlotCard
                    key={slot.roleSlotGuid}
                    slot={slot}
                    userMap={userMap}
                    ownerGuid={ownerGuid}
                    canEdit={canEdit}
                    bundleName={slotBundleNameMap.get(slot.roleSlotGuid.toUpperCase()) ?? null}
                    onEdit={(s) => setEditingSlot(s)}
                    onAssign={(s) => setAssigningSlotGuid(s.roleSlotGuid)}
                    onClose={handleCloseSlot}
                    onToggleVisibility={handleToggleVisibility}
                    onViewAssignment={(a, s) => setDetailAssignment({ assignment: a, slot: s })}
                    isDragging={dragSlotGuid === slot.roleSlotGuid}
                    isDragOver={dragOverSlotGuid === slot.roleSlotGuid}
                    onDragStart={handleDragStart(slot.roleSlotGuid)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver(slot.roleSlotGuid)}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop(slot.roleSlotGuid, draftSlots)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── RACI view (matrix of roles vs tasks) ── */}
      {viewMode === "raci" && (
        <TeamRaciView projectGuid={projectGuid} projectName={projectName} canEdit={canEdit} />
      )}

      {/* ── Digest view (personalized RACI-scoped task briefing) ── */}
      {viewMode === "digest" && (
        <RoleDigestView
          projectGuid={projectGuid}
          roleSlotGuids={myRoleSlotGuids}
          currentUserGuid={currentUserGuid}
          debugMode={debugMode}
        />
      )}

      {/* Archived (closed) roles — only shown on Cards view */}
      {viewMode === "cards" && closedSlots.length > 0 && canEdit && (
        <div className={styles.archivedSection}>
          <button
            type="button"
            className={styles.archivedToggle}
            onClick={() => setShowArchived((v) => !v)}
          >
            <span className={styles.archivedToggleIcon}>
              {showArchived ? "▾" : "▸"}
            </span>
            {t("archived.title")}
            <span className={styles.archivedCount}>{closedSlots.length}</span>
          </button>
          {showArchived && (
            <div className={styles.archivedList}>
              {closedSlots.map((slot) => (
                <div key={slot.roleSlotGuid} className={styles.archivedCard}>
                  <div className={styles.archivedCardInfo}>
                    <span className={styles.archivedLabel}>{slot.roleLabel}</span>
                    <span className={styles.archivedCategory}>{slot.roleCategory}</span>
                  </div>
                  <button
                    type="button"
                    className={styles.reopenBtn}
                    onClick={() => handleReopenSlot(slot)}
                  >
                    {t("archived.reopen")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Role editor modal (create or edit) */}
      {(showAddForm || editingSlot) && (
        <RoleSlotEditor
          mode="project"
          existingSlot={editingSlot}
          allSlots={slots}
          projectGuid={projectGuid}
          currentBundleGuid={currentBundleGuidForEditing}
          onSave={handleSaveRole}
          onSaveAndAdd={!editingSlot ? handleSaveAndAdd : undefined}
          onDelete={editingSlot ? () => handleCloseSlot(editingSlot) : undefined}
          deleteError={closeError}
          onClose={() => {
            setShowAddForm(false);
            setEditingSlot(null);
            setCloseError(null);
          }}
        />
      )}

      {/* Assignment modal */}
      {assigningSlot && (
        <RoleAssignmentModal
          slot={assigningSlot}
          allUsers={allUsers}
          onAssign={handleAssignUser}
          onInvite={handleInviteUser}
          onRemove={handleRemoveAssignment}
          onUpdateVisibility={handleUpdateVisibility}
          onRevokeInvitation={handleRescindInvitation}
          onClose={() => setAssigningSlotGuid(null)}
          // DEV-1039 iter 3. Resolve the slot's bundle from slotGrants +
          // allBundles so the modal can run the 2FA cross-check (dormant
          // warning when a picked user lacks TOTP). Returns null when the
          // slot has no active grant — modal suppresses 2FA UI in that case.
          slotPermissionSet={(() => {
            const slotTarget = assigningSlot.roleSlotGuid.toUpperCase();
            const grant = slotGrants.find(
              (g) =>
                g.roleSlotGuid != null &&
                g.roleSlotGuid.toUpperCase() === slotTarget &&
                (g.status === undefined || g.status === "active"),
            );
            if (!grant) return null;
            return (
              allBundles.find(
                (b) =>
                  b.permissionSetGuid.toUpperCase() ===
                  grant.permissionSetGuid.toUpperCase(),
              ) ?? null
            );
          })()}
          projectName={projectName}
          currentUserGuid={currentUserGuid}
          communityNameByGuid={communityNameByGuid}
        />
      )}

      {/* Role assignment detail modal (read-only dossier) */}
      <RoleAssignmentDetailModal
        isOpen={detailAssignment !== null}
        onClose={() => setDetailAssignment(null)}
        projectRole={detailAssignment?.assignment ?? null}
        slotContext={detailAssignment?.slot ? {
          roleLabel: detailAssignment.slot.roleLabel,
          roleCategory: detailAssignment.slot.roleCategory,
          description: detailAssignment.slot.description ?? null,
          verificationLevel: detailAssignment.slot.verificationLevel,
          skillsRequired: detailAssignment.slot.skillsRequired,
          timeCommitment: detailAssignment.slot.timeCommitment ?? null,
          durationType: detailAssignment.slot.durationType,
          expectedStartDate: detailAssignment.slot.expectedStartDate ?? null,
          expectedEndDate: detailAssignment.slot.expectedEndDate ?? null,
        } : null}
        projectName={projectName}
        tasks={roleDetailData.tasks}
        allProjectGanttTasks={roleDetailData.allProjectGanttTasks}
        allTaskRoleAssignments={roleDetailData.allTaskRoleAssignments}
        hierarchy={roleDetailData.hierarchy}
        userDisplayNames={roleDetailData.userDisplayNames}
      />
    </div>
  );
};

export default ProjectTeamPanel;
